import type { MouseEvent } from 'react';

/**
 * The id every shell's `<main>` carries (alongside `tabIndex={-1}`) so
 * `SkipLink` can find and focus it without a ref or any other per-shell
 * wiring — `AppShell`, `LoginGate`, `PublicFixturesShell`, `PublicTryShell`,
 * `PublicRecordShell` (all in `App.tsx`), `LandingPage` and
 * `UnsubscribeRoute` each render exactly one `<main id={MAIN_CONTENT_ID}>`.
 */
export const MAIN_CONTENT_ID = 'main-content';

/**
 * The skip link every shell with a `<main>` renders first in its DOM (T-100
 * on `AppShell`; extracted for T-102 so the six signed-out shells get the
 * same thing instead of their own copies). `preventDefault` plus an
 * explicit `.focus()` on the target, rather than relying on the browser to
 * focus the `href="#main-content"` fragment on its own: that native
 * behaviour is inconsistent across browsers and isn't something jsdom does
 * at all, so this is what actually makes the jump land rather than just
 * scroll. Looking the target up by id rather than taking a ref prop is what
 * lets every shell use this with no configuration of its own.
 */
export function SkipLink() {
  function focusMain(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    document.getElementById(MAIN_CONTENT_ID)?.focus();
  }

  return (
    <a href={`#${MAIN_CONTENT_ID}`} className="skip-link" onClick={focusMain}>
      Skip to main content
    </a>
  );
}
